import * as React from "react";

import * as R from "ramda";

import GenButton from "../Gen/Button";
import GenPostButton from "../Gen/PostButton";

import {
  inlineHTMLToJSON,
  inlineJSONToHTML,
} from "../../src/convert";

import {
  DisplayMode,
  displayModeDisplayName,
} from "../../src/display_mode";

import {
  modifyNodeValueByUIDAndKey,
} from "../../src/doc_helpers";

import {
  setStyleArg,
} from "../../src/inline_json";

function isCurrentlyEditingText(currentlyEditing: CurrentlyEditing | undefined) {
  return currentlyEditing?.uid && currentlyEditing?.range && currentlyEditing?.key;
}

interface DocTopBarProps {
  editState: EditStateTrue;
  currentDocument: CurrentDocument;
  displayState: DisplayState;
  currentVersion: CurrentVersion;
  currentUser: CurrentUser;
  currentRoleDefinition: CurrentRoleDefinition;
}

export default function DocTopBar(props: DocTopBarProps) {
  const {
    editState,
    currentDocument,
    displayState,
    currentVersion,
  } = props;

  const {
    handleStyle,
  } = currentVersion._actions;

  const {
    currentlyEditing,
  } = editState;

  const styleButtons = ([
    ["bold", "b"],
    ["italic", "i"],
    ["underline", "u"],
    ["subscript", "sub"],
    ["superscript", "sup"],
    ["link", "a"],
  ] as const).map(([styleName, tagName]) => {
    let showAsActive = false;
    let extraControls;
    if (currentlyEditing) {
      const activeStyle = editState.currentlyActiveStyles?.find((style) => style.type === tagName);
      const newlyActive = currentlyEditing?.newlyActiveStyles?.find((style) => style.type === tagName);
      const newlyInactive = currentlyEditing?.newlyInactiveStyles?.find((style) => style.type === tagName);

      showAsActive = (!!activeStyle || !!newlyActive) && !newlyInactive;

      if (tagName === "a" && showAsActive) {
        let handleChange, linkValue;
        if (activeStyle) {
          // Update Current Style
          linkValue = (activeStyle as StyleLink).args?.href || "";
          handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newLinkValue = event.target.value;
            currentVersion._actions.modifyEditJSON((json) => {
              if (!currentlyEditing) { return; }

              return modifyNodeValueByUIDAndKey(json, currentlyEditing.uid, currentlyEditing.key, (inlineHTML: InlineHtmlContent) => {
                const newInlineJSON = setStyleArg(inlineHTMLToJSON(inlineHTML), tagName, currentlyEditing.range.start, "href", newLinkValue);
                return inlineJSONToHTML(newInlineJSON);
              });
            });
          };
        } else {
          // Update newlyActiveStyles
          linkValue = (newlyActive as StyleLink).args?.href || "";
          handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newLinkValue = event.target.value;
            const newlyActiveStyles  = currentlyEditing.newlyActiveStyles || [];
            editState.setCurrentlyEditing({...currentlyEditing,
              newlyActiveStyles: newlyActiveStyles.map((style) => style.type === "a" ? R.assoc("args", {href: newLinkValue}, style) : style),
            });
          };
        }

        extraControls = (
          <input
            value={linkValue}
            onChange={handleChange}
            style={{width: "200px"}}
            type='text'
            onFocus={() => editState.setCurrentlyEditing({...currentlyEditing,
            // selectionMenu: 'link' // TODO: Fix
          })}
            onBlur={() => editState.setCurrentlyEditing({...currentlyEditing,
            // selectionMenu: undefined // TODO: Fix
          })}
          />
        );
      }
    }

    return (
      <React.Fragment key={styleName}>
        <GenButton
          spanClasses={showAsActive ? "bg-sky-600" : "bg-white" }
          key={styleName}
          highlighted={showAsActive}
          disabled={!isCurrentlyEditingText(currentlyEditing)}
          name={<i className={`fa fa-${styleName}`} />}
          onClick={() => handleStyle(tagName, editState.currentlyEditing)}
        />
        {extraControls}
      </React.Fragment>
    );
  });

  return (
    <div className="doc-top-bar">
      <div>
        <GenButton
          name={<i className="fa fa-save" />}
          highlighted
          disabled={!editState.hasUnsavedChanges}
          onClick={editState.save}
        />
        {displayState.documentType !== "Activity" && (
          <GenPostButton
            name="Publish"
            highlighted
            disabled={editState.hasUnsavedChanges}
            url={`/documents/${currentDocument.id}/publish`}
            data={{}}
          />
        )}
        <div className="doc-top-bar-divider"/>
        <select className="my-1 doc-mode-selection" value={displayState.displayMode} onChange={(e) => displayState.setDisplayMode(e.target.value as DisplayMode)}>
          {Object.values(DisplayMode).map((displayMode) => <option key={displayMode} value={displayMode}>{displayModeDisplayName(displayMode)}</option>)}
        </select>
        <div className="doc-top-bar-divider"/>
        <GenButton
          spanClasses="bg-white"
          name={<i className="fa fa-arrow-rotate-left" />}
          disabled={editState.undoPossible}
          onClick={editState.undo}
        />
        <GenButton
          spanClasses="bg-white"
          name={<i className="fa fa-arrow-rotate-right" />}
          disabled={editState.redoPossible}
          onClick={editState.redo}
        />
        <div className="doc-top-bar-divider"/>
        <GenButton
          spanClasses={editState.currentlyEditingAncestors?.some((node) => node.type === "regularList") ? "bg-sky-600" : "bg-white"}
          name={<i className="fa fa-list-ul" />}
          disabled={!isCurrentlyEditingText(currentlyEditing)}
          onClick={() => currentVersion._actions.handleNewListInsert("regularList")}
        />
        <GenButton
          spanClasses={editState.currentlyEditingAncestors?.some((node) => node.type === "numberedList") ? "bg-sky-600" : "bg-white"}
          name={<i className="fa fa-list-ol" />}
          disabled={!isCurrentlyEditingText(currentlyEditing)}
          onClick={() => currentVersion._actions.handleNewListInsert("numberedList")}
        />
        <GenButton
          spanClasses={editState.currentlyEditingNode?.type === "image" ? "bg-sky-600" : "bg-white"}
          name={<i className="fa fa-image" />}
          disabled={!isCurrentlyEditingText(currentlyEditing)}
          highlighted={editState.currentlyEditingNode?.type === "image"}
          onClick={() => {
            const newUid = currentVersion._actions.handleNewMediaInsert("image", currentDocument);
            if (newUid) { displayState.setOpenMenu({name: "MEDIA_MENU", uid: newUid, mediaType: "image"}); }
          }}
        />
        <GenButton
          spanClasses={editState.currentlyEditingNode?.type === "audio" ? "bg-sky-600" : "bg-white"}
          name={<i className="fa fa-music" />}
          disabled={!isCurrentlyEditingText(currentlyEditing)}
          highlighted={editState.currentlyEditingNode?.type === "audio"}
          onClick={() => {
            const newUid = currentVersion._actions.handleNewMediaInsert("audio", currentDocument);
            if (newUid) { displayState.setOpenMenu({name: "MEDIA_MENU", uid: newUid, mediaType: "audio"}); }
          }}
        />
        <GenButton
          spanClasses={editState.currentlyEditingNode?.type === "video" ? "bg-sky-600" : "bg-white"}
          name={<i className="fa fa-video" />}
          disabled={!isCurrentlyEditingText(currentlyEditing)}
          highlighted={editState.currentlyEditingNode?.type === "video"}
          onClick={() => {
            const newUid = currentVersion._actions.handleNewMediaInsert("video", currentDocument);
            if (newUid) { displayState.setOpenMenu({name: "MEDIA_MENU", uid: newUid, mediaType: "video"}); }
          }}
        />
        <div className="doc-top-bar-divider"/>
        {styleButtons}
      </div>
      <div />
    </div>
  );
}
